import React from 'react';
import styled from 'styled-components';
import {useHistory, useParams} from 'react-router-dom';
import {Modal} from 'components/antd';
import {
  AutomaticMailAttributes,
  AutomaticMailStep,
  AutomaticMailStepAttributes,
  Step,
  StepAttributes,
  useUpdateAutomaticMailMutation,
  useUpdateStepMutation,
} from 'api';
import AutomaticMailForm from './AutomaticMailForm';
import StepForm from './StepForm';
import {useFormik} from 'formik';
import useAutomaticMailForm from './useAutomaticMailForm';
import useStepForm from './useStepForm';

interface AutomaticMailStepType extends Step {
  steppable: AutomaticMailStep;
}

interface Props {
  step: AutomaticMailStepType;
  visible: boolean;
}

type StepFormAttributes = StepAttributes &
  AutomaticMailStepAttributes & {scheduleType: string};

const AutomaticMailModalComponent = ({step, visible}: Props) => {
  const [formType, setFormType] = React.useState('step');
  const {poolId, workflowId, stepId} = useParams<{
    poolId: string;
    workflowId: string;
    stepId: string;
  }>();
  const history = useHistory();
  const onClose = React.useCallback(
    () =>
      history.push(
        `/pools/${poolId}/workflows/${workflowId}${location.search}`,
      ),
    [poolId, workflowId],
  );

  const {
    convertValues,
    initialValues: initialValuesStep,
    validationSchema: validationSchemaStep,
  } = useStepForm({
    step,
  });
  const [updateStep, {loading: loadingStep}] = useUpdateStepMutation({
    onCompleted: () => onClose(),
  });
  const onSubmitStep = (values: StepFormAttributes) => {
    const attributes = convertValues(values);
    const isContinuedByEvent = values.isContinuedByEvent;
    const eventToContinue = values.eventToContinue;
    updateStep({
      variables: {
        uuid: stepId,
        attributes: attributes,
        automaticMailStepAttributes: {
          isContinuedByEvent,
          eventToContinue,
        },
      },
    });
  };
  const formikStep = useFormik({
    enableReinitialize: true,
    initialValues: initialValuesStep,
    validationSchema: validationSchemaStep,
    onSubmit: (values) => {
      onSubmitStep(values);
      onSubmitAutomaticMail(formikAutomaticMail.values);
    },
  });

  const mail = step.steppable?.automaticMail;
  const {
    convertBodyHtml,
    initialValues: initialValuesAutomaticMail,
    validationSchema: validationSchemaAutomaticMail,
  } = useAutomaticMailForm({
    step,
  });
  const [updateAutomaticMail, {loading: loadingAutomaticMail}] =
    useUpdateAutomaticMailMutation({
      onCompleted: () => onClose(),
    });
  const onSubmitAutomaticMail = (values: AutomaticMailAttributes) => {
    values.bodyHtml = convertBodyHtml(values.bodyHtml);
    updateAutomaticMail({
      variables: {id: mail.id, attributes: values},
    });
  };
  const formikAutomaticMail = useFormik({
    enableReinitialize: true,
    initialValues: initialValuesAutomaticMail,
    validationSchema: validationSchemaAutomaticMail,
    onSubmit: (values) => {
      onSubmitAutomaticMail(values);
      onSubmitStep(formikStep.values);
    },
  });

  return (
    <AutomaticMailModal
      title={step?.name}
      visible={visible}
      onCancel={onClose}
      width={680}
      footer={null}
      maskClosable={false}>
      <Tabs>
        <TabItem
          active={formType === 'step'}
          onClick={() => setFormType('step')}>
          ステップ
        </TabItem>
        <TabItem
          active={formType === 'mail'}
          onClick={() => setFormType('mail')}>
          メール
        </TabItem>
      </Tabs>
      <Content>
        {formType === 'step' && (
          <StepForm step={step} formik={formikStep} loading={loadingStep} />
        )}
        {formType === 'mail' && (
          <AutomaticMailForm
            step={step}
            formik={formikAutomaticMail}
            loading={loadingAutomaticMail}
          />
        )}
      </Content>
    </AutomaticMailModal>
  );
};

const AutomaticMailModal = styled(Modal)`
  h3 {
    font-weight: bold;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-modal-footer {
    border: none;
  }
`;

const Tabs = styled.div`
  display: flex;
  border-bottom: solid 1px #e1e6eb;
  padding: 12px 24px 0;
`;

const TabItem = styled.div<{active?: boolean}>`
  text-align: center;
  border-bottom: solid 3px;
  border-color: ${({active}) =>
    active ? 'var(--text-color-1)' : 'transparent'};
  padding: 0;
  margin-right: 2rem;
  cursor: pointer;

  font-size: 12px;
  font-weight: bold;
  line-height: 30px;
  color: ${({active}) =>
    active ? 'var(--text-color-1)' : 'var(--text-color-3)'};
  display: flex;
  width: 64px;
  flex-direction: column;
  align-items: center;
`;

const Content = styled.div`
  padding: 24px 24px 0 24px;
`;

export default AutomaticMailModalComponent;
